import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, signal, Type } from '@angular/core';
import { ServiceStateEnum } from '@mwe/constants';
import { BackendProcessStore, StepInterface, StepStateInterface } from '@mwe/services';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { TranslateDirective, TranslateService } from '@ngx-translate/core';
import { NgClass, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { NextStepsComponent } from '../../next-steps/next-steps.component';
import { AddStaticImagePathPipe } from '@mwe/ui/pipes';
import { BaseProductAddModule } from '@mwe/features/product-add';
import { InlineNotificationComponent } from '../../inline-notification/inline-notification.component';
import { BadgeType } from '@mwe/models';
import { BadgeComponent } from '../../badge/badge/badge.component';

type StepRepresentation = { step: StepInterface; state?: StepStateInterface };
export type InfoStep = Pick<StepInterface, 'title' | 'label'> & Partial<Pick<StepInterface, 'translationParams'>>;

@Component({
  selector: 'mwe-backend-process-presentation-component',
  templateUrl: './backend-process-presentation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BasicCardComponent,
    BadgeComponent,
    TranslateDirective,
    NgTemplateOutlet,
    NextStepsComponent,
    NgComponentOutlet,
    NgClass,
    AddStaticImagePathPipe,
    BaseProductAddModule,
    InlineNotificationComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BackendProcessPresentationComponent {
  private readonly process = inject(BackendProcessStore);
  private readonly translateService = inject(TranslateService);

  readonly processTitle = input<string>('');
  readonly infoSteps = input<InfoStep[]>([]);
  readonly infoTitleKey = input<string>('summary.whatNext');
  readonly errorTitleKey = input<string>('');
  readonly errorMsgTitleKey = input<string>('process.default.error.title');
  readonly errorMsgKey = input<string>('process.default.error.message');
  readonly ctaLabelKey = input<string>(undefined);
  readonly ctaOnClick = input<string>(undefined);
  readonly errorMsgRetryKey = input<string>('process.default.error.message_retry');
  readonly workInProgressMsgKey = input<string>('process.default.loading.title');
  readonly workInProgressSubtextKey = input<string>('');
  readonly successMsgKey = input<string>('process.default.success.title');
  readonly successSubtextKey = input<string>('process.default.success.message');

  /**
   * Optional component to render the steps
   * Will get step: StepInterface and state: StepStateInterface as inputs
   */
  readonly stepRenderer = signal<Type<unknown>>(null);
  readonly alertPlaceholders = signal<Map<string, string>>(undefined);

  readonly retryAllowed = computed(() => this.process.retryAllowed());
  readonly showSuccessState = computed(() => !this.showLoadingState() && this.process.viewState() === ServiceStateEnum.SUCCESS);
  readonly steps = computed<StepRepresentation[]>(() => {
    const steps = Array.from(this.process.steps().entries());
    return steps.map(([step, state]) => ({ step, state }));
  });
  readonly showLoadingState = computed(() => {
    const processState = this.process.viewState();
    return processState === ServiceStateEnum.INIT || processState === ServiceStateEnum.LOADING;
  });
  readonly showErrorState = computed(
    () =>
      !this.showLoadingState() &&
      (this.process.viewState() === ServiceStateEnum.ERROR || this.process.viewState() === ServiceStateEnum.FAILED),
  );

  getIconClassForStep(item: StepRepresentation): string | undefined {
    const { step, state } = item;
    const icon = step.iconMap ? step.iconMap.get(state.state) : undefined;
    return icon ? `fas ${icon} ` : undefined;
  }

  getBadgeTypeForStep(item: StepRepresentation): BadgeType | undefined {
    const { step, state } = item;
    const type = step.stateMap ? step.stateMap.get(state.state) : undefined;
    return type as BadgeType;
  }

  getBadgeLabelForStep(item: StepRepresentation): string | undefined {
    const { step, state } = item;
    const label = step.stateLabelMap ? step.stateLabelMap.get(state.state) : undefined;
    return label;
  }

  getTitleForStep(item: StepRepresentation): string {
    const { step, state } = item;
    const defaultTitle = step.title;
    const stateTitle = step.textMap.get(state.state)?.title;
    return stateTitle ?? defaultTitle;
  }

  getLabelsForStep(item: StepRepresentation): string[] {
    const { step, state } = item;
    const defaultLabel = step?.label ? (Array.isArray(step.label) ? step.label : [step.label]) : undefined;
    let stateLabel = step.textMap.get(state.state)?.label;
    stateLabel = stateLabel ? (Array.isArray(stateLabel) ? stateLabel : [stateLabel]) : undefined;
    return stateLabel ?? defaultLabel;
  }

  handleCTAClick(): void {
    const urlKey = this.ctaOnClick();
    if (urlKey) {
      const url = this.translateService.instant(urlKey);
      window.open(url, '_blank', 'noopener');
    }
  }
}
